import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import LockIcon from '@mui/icons-material/Lock';

import { partReplenishmentOrders, vendorShipments } from '../../services/dataClient';
import { fieldSx } from '../../theme/fieldStyles';

const CARRIERS = ['FedEx', 'UPS', 'DHL', 'USPS', 'OnTrac', 'Freight'];

// Session-level counter so shipment numbers stay unique across multiple creates
let _vsSeq = 0;

// ─── Field style helpers ───────────────────────────────────────────────────────

const lockedSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '0.875rem',
    backgroundColor: '#f8fafc',
    '& fieldset': { borderColor: 'var(--grey-200)' },
    '&:hover fieldset': { borderColor: 'var(--grey-200)' },
  },
};

// Read-only field with lock icon + grey background
function LockedField({ label, value }) {
  return (
    <TextField
      size="small"
      label={label}
      value={value ?? ''}
      fullWidth
      InputProps={{
        readOnly: true,
        endAdornment: (
          <InputAdornment position="end">
            <LockIcon sx={{ fontSize: 14, color: 'var(--grey-300)' }} />
          </InputAdornment>
        ),
      }}
      sx={lockedSx}
    />
  );
}

const EMPTY_ORDER_FIELDS = {
  partReplenishmentOrderId: '',
  vendorName:               '',
  stockLocation:            '',
};

const EMPTY_EDITABLE = {
  carrier:               'FedEx',
  trackingNumber:        '',
  estimatedDeliveryDate: '',
  notes:                 '',
};

/**
 * Shared Create Vendor Shipment modal
 .*
 * When `prefill` is omitted (Supplier Shipments page): all fields are editable,
 * identical behaviour to the original inline dialog
 .*
 * When `prefill` is provided (Part Replenishment pages): the linked order, vendor,
 * part, and customer fields are locked. Only carrier, tracking #, est. delivery
 * date, and notes are editable
 .*
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   onSubmit: (newShipment: object) => void,
 *   prefill?: {
 *     partReplenishmentOrderId: string,
 *     partReplenishmentOrderNumber: string,
 *     vendorName: string,
 *     stockLocation: string,
 *   },
 * }} props
 */
export default function CreateVendorShipmentModal({ open, onClose, onSubmit, prefill }) {
  const isPrefilled = Boolean(prefill);

  // Editable fields — shared across both modes
  const [editable, setEditable] = useState(EMPTY_EDITABLE);
  // Order-level fields — only used when no prefill
  const [orderFields, setOrderFields] = useState(EMPTY_ORDER_FIELDS);

  // Reset all internal state whenever the modal opens
  useEffect(() => {
    if (open) {
      setEditable(EMPTY_EDITABLE);
      setOrderFields(EMPTY_ORDER_FIELDS);
    }
  }, [open]);

  const setE = (field) => (e) => setEditable((prev) => ({ ...prev, [field]: e.target.value }));

  // Auto-fill order fields when a Part Replenishment Order is selected (no-prefill mode only)
  const handlePartReplenishmentOrderChange = (e) => {
    const orderId = e.target.value;
    if (!orderId) {
      setOrderFields(EMPTY_ORDER_FIELDS);
      return;
    }
    const order = partReplenishmentOrders.find((o) => o.id === orderId);
    if (!order) return;
    setOrderFields({
      partReplenishmentOrderId: orderId,
      vendorName:    order.vendorName    ?? '',
      stockLocation: order.stockLocation ?? '',
    });
  };

  // Resolved values — prefill wins when present
  const vendorName               = isPrefilled ? (prefill.vendorName    ?? '') : orderFields.vendorName;
  const stockLocation            = isPrefilled ? (prefill.stockLocation ?? '') : orderFields.stockLocation;
  const partReplenishmentOrderId = isPrefilled ? prefill.partReplenishmentOrderId : orderFields.partReplenishmentOrderId;

  const isValid =
    partReplenishmentOrderId && vendorName &&
    editable.carrier && editable.trackingNumber && editable.estimatedDeliveryDate;

  const handleCreate = () => {
    const seq = _vsSeq++;
    const newShipment = {
      id:                  `vsph-new-${Date.now()}`,
      shipmentNumber:      `RS${8001 + vendorShipments.length + seq}`,
      vendorName,
      stockLocation,
      customerDestination: stockLocation,
      carrier:             editable.carrier,
      trackingNumber:        editable.trackingNumber,
      estimatedDeliveryDate: editable.estimatedDeliveryDate,
      status:                'Active',
      notes:                 editable.notes || null,
      partReplenishmentOrderId:     partReplenishmentOrderId || null,
      partReplenishmentOrderNumber: isPrefilled
        ? prefill.partReplenishmentOrderNumber
        : (orderFields.partReplenishmentOrderId
            ? partReplenishmentOrders.find((o) => o.id === orderFields.partReplenishmentOrderId)?.orderNumber ?? null
            : null),
    };
    onSubmit(newShipment);
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => { if (reason !== 'backdropClick') onClose(); }}
      maxWidth="sm"
      fullWidth
    >
      {/* ── Title ── */}
      <DialogTitle
        sx={{ display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          px: 3,
          pt: 2,
          pb: isPrefilled ? 1.5 : 1,
          borderBottom: '1px solid var(--grey-200)',
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--grey-900)', lineHeight: 1.3 }}>
            Create Replenishment Shipment
          </Typography>
          {isPrefilled && (
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: '2px' }}>
              Linked to {prefill.partReplenishmentOrderNumber}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1, mt: -0.25 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      {/* ── Body ── */}
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important', pb: 1 }}>

        {/* No-prefill mode: order selection drives supplier + stock location */}
        {!isPrefilled && (
          <>
            <FormControl size="small" fullWidth sx={fieldSx} required>
              <InputLabel>Linked Replenishment Order</InputLabel>
              <Select
                label="Linked Replenishment Order"
                value={orderFields.partReplenishmentOrderId}
                onChange={handlePartReplenishmentOrderChange}
              >
                {partReplenishmentOrders.map((o) => (
                  <MenuItem key={o.id} value={o.id}>{o.orderNumber} — {o.vendorName}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <LockedField label="Supplier"       value={orderFields.vendorName}    />
            <LockedField label="Stock Location" value={orderFields.stockLocation} />
          </>
        )}

        {/* Prefill mode: locked context fields */}
        {isPrefilled && (
          <>
            <LockedField label="Linked Replenishment Order" value={prefill.partReplenishmentOrderNumber} />
            <LockedField label="Supplier"                   value={prefill.vendorName}    />
            <LockedField label="Stock Location"             value={prefill.stockLocation} />
          </>
        )}

        {/* Shared editable fields */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" fullWidth sx={fieldSx}>
            <InputLabel>Carrier</InputLabel>
            <Select label="Carrier" value={editable.carrier} onChange={setE('carrier')}>
              {CARRIERS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            size="small" label="Tracking #"
            value={editable.trackingNumber}
            onChange={setE('trackingNumber')}
            fullWidth sx={fieldSx}
          />
        </Box>

        <TextField
          size="small"
          label="Est. Delivery Date"
          type="date"
          value={editable.estimatedDeliveryDate}
          onChange={setE('estimatedDeliveryDate')}
          fullWidth
          sx={fieldSx}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          size="small"
          label="Notes (optional)"
          value={editable.notes}
          onChange={setE('notes')}
          multiline
          rows={2}
          fullWidth
          sx={fieldSx}
        />
      </DialogContent>

      {/* ── Actions ── */}
      <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
        <Typography
          onClick={onClose}
          sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}
        >
          Cancel
        </Typography>
        <Button
          variant="contained"
          size="small"
          disabled={!isValid}
          onClick={handleCreate}
          sx={{ backgroundColor: '#009688',
            '&:hover': { backgroundColor: '#00796B' },
            '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            boxShadow: 'none',
            px: 2,
          }}
        >
          Create Shipment
        </Button>
      </DialogActions>
    </Dialog>
  );
}
